// Liste Denetimi — TAKI. Tisort denetimiyle ayni mantik: secilen urunler tek
// ekranda gozden gecirilir, eksikler engel/uyari olarak gorunur, hazir
// olanlar tek tikla (ya da toplu) Etsy taslagina cevrilir.
//
// Takida tasarim/mockup yok: urunun URETILMIS konsept fotograflari listenin
// gorselleridir. Fiyat, varyasyon ve kisisellestirme kurallari Listing
// Builder ile ORTAKTIR (lib/jewelryListing) — iki ekran ayni taslagi uretir.
//
// Yapilan degisiklikler tarayicida oturum olarak saklanir: sayfa yenilense ya
// da "Duzenle" ile baska sekmeye gecilse de kaybolmaz.

import { useEffect, useMemo, useRef, useState } from "react";
import { EtsyListingPreviewBox } from "../components/listing/EtsyListingPreview";
import { Link, useSearchParams } from "react-router-dom";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, ClipboardCheck, ExternalLink,
  Loader2, Pencil, Plus, Search, Sparkles, Star, Truck, X, XCircle,
} from "lucide-react";
import { useShopStore } from "../store/shop.store";
import { useShippingProfilesByShop, shippingProfileColor } from "../lib/shippingProfiles";
import { apiClient } from "../lib/api";
import { authHeaders, getBackendUrl } from "../lib/http";
import { useShopVertical } from "../lib/useShopVertical";
import { useJewelryTypes } from "../lib/useJewelryTypes";
import { useShotProducts, type ShotProduct } from "../lib/useShotProducts";
import { ImageLightbox } from "../components/ImageLightbox";
import { EtsyPropertiesEditor } from "../components/listing/EtsyPropertiesEditor";
import { PersonalizationEditor } from "../components/listing/PersonalizationEditor";
import type { PersonalizationQuestion } from "@erc/shared";
import {
  useTaxonomyPropsMap, baseProps, mergeProps, aiProps, effectiveTemplateDefaults,
  type EtsyTaxProp, type ListingProp,
} from "../lib/etsyProperties";
import { checkListingReadiness, titleWarnings } from "@erc/shared";
import {
  TITLE_LIMITS, applyPattern, productCost, productShipping, renderDescriptionFooter, trailingImages, LIST_PRICE_FACTOR, entryComboPrices, scaleVariationPrices, type EntryVariant,
  DEFAULT_TARGET_MARGIN, marginRule, etsyProfit, readLastMargin, writeLastMargin, etsyTitleCase,
  resolveOptionSets, buildVariations, buildPersonalization, etsyLimitError, effectiveOptionRefs,
  attributeLines, productMaterials,
  type OptionSet, type TemplateRow,
  soleLeadingInspiration, sameTypeTaxonomies,
} from "../lib/jewelryListing";

/** Taslak videosu: urun fotograflarindan 1:1, 8 sn slayt (tisort akisiyla ayni) */
const DRAFT_VIDEO = { aspectRatio: "1:1" as const, duration: 8 as const };
const abs = (u?: string) => (!u ? "" : u.startsWith("http") || u.startsWith("data:") ? u : `${getBackendUrl()}${u}`);
const SESSION_KEY = "erc:jewelry-review:session";
/** Toplu Studio da ayni anahtari gosterir/degistirir */
export const AUTO_AI_KEY = "erc_review_auto_ai";
const LAST_TPL_KEY = "erc:jewelry-review:last-template";
/** Son secilen sablonu hatirla (sayfa yeniden acildiginda varsayilan) */
const tplListeners = new Set<(id: string) => void>();
function rememberTemplate(id: string) {
  try { localStorage.setItem(LAST_TPL_KEY, id); } catch { /* */ }
  for (const f of tplListeners) f(id);
}
const SHIP_TARGETS_KEY = "erc:jewelry-review:ship-targets";

/** Coklu kargo profili: secilen her profil icin listenin bir kopyasi, profilin
 *  magazasinda olusturulur */
type ShipTarget = { shopId: string; profileId: string };
const targetKey = (t: ShipTarget) => `${t.shopId}:${t.profileId}`;
const TITLE_MAX = TITLE_LIMITS.etsy;

/** Kartta kullanicinin yaptigi degisiklikler (verilmeyen alan varsayilandir) */
interface Override {
  templateId?: string | null; // null: sablonsuz
  /** Etsy kategorisinin kaynagi: ilham listesinin listingId'si ya da "__template" */
  taxonomyFrom?: string;
  title?: string;
  description?: string;
  tags?: string[];
  /** Etsy malzemeleri (AI ya da elle) */
  materials?: string[];
  images?: string[];
  /** Eski: fiyat carpani (artik kullanilmiyor, kayitli override'lar bozulmasin diye duruyor) */
  multiplier?: number;
  /** Etsy hedef net kar marji (%) — urune ozel */
  marginPct?: number;
  /** En ucuz varyasyon (null: bu urunde kapali; verilmezse sablonun varsayilani) */
  entryVariant?: EntryVariant | null;
  /** Etsy kategori ozellikleri (AI ya da elle); yoksa kural + sablondan hesaplanir */
  etsyProps?: ListingProp[];
  /** Etsy "styles" (en fazla 2) */
  styles?: string[];
  /** Kisisellestirme sorulari elle duzenlendiyse (bos dizi: kisisellestirme yok) */
  personalization?: PersonalizationQuestion[];
  /** Listeden cikarilan varyasyon degerleri: eksen adi -> degerler */
  excludedOptions?: Record<string, string[]>;
  includeSupplier?: boolean;
  createdDraftId?: string;
  /** Coklu kargo: kopyasi olusturulmus hedefler (yarida kalirsa tekrar olusturulmaz) */
  copiedTargets?: string[];
  /** SEO deneyi: hangi stratejiyle yazildi + AI ciktisi (elle duzeltme tespiti icin); taslaga yazilir */
  seo?: SeoRecord;
}

type Strategy = "A" | "B" | "C";
interface SeoRecord {
  strategy: Strategy;
  /** Sistemin sirayla atadigi strateji (kullanici degistirdiyse farkli) */
  assigned: Strategy;
  aiTitle: string; aiTags: string[];
  poolKeywords: string[]; poolTop: string[]; inspirationIds: string[];
  generatedAt: string;
}
const STRATEGIES: Array<{ key: Strategy; label: string; hint: string }> = [
  { key: "A", label: "A · Talep", hint: "Başlık en yüksek talepli ifadeyle başlar" },
  { key: "B", label: "B · Uzun kuyruk", hint: "Başlık düşük rekabetli, ürünü tam tarif eden ifadeyle başlar" },
  { key: "C", label: "C · İlham kalıbı", hint: "En çok satan ilham listelerinin başlık yapısı ve ortak tag'leri (eski listelerin yöntemi; atanmaz, yalnız elle)" },
];
/** Sirayla atama: urun anahtarindan sabit (ayni urun hep ayni stratejiyi alir). 29/10 karari: B ana (2/3),
 *  A kontrol (1/3); C atanmaz — mevcut listeler C yontemiyle yazildi, karsilastirmada gecmis temel olur */
function assignedStrategy(key: string): Strategy {
  let h = 0;
  for (const ch of key) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return (["B", "B", "A"] as const)[h % 3];
}

type Shot = { id: string; imageUrl: string; isPrimary: boolean };

/** Toplu Studio'nun otomatik gonderimi: urunleri denetim oturumuna ekler (sayfa acilinca listede) */
export function queueForReview(keys: string[]) {
  const cur = loadSession();
  try { localStorage.setItem(SESSION_KEY, JSON.stringify({ ...cur, selected: [...new Set([...cur.selected, ...keys])] })); } catch { /* dolu */ }
}

function loadSession(): { selected: string[]; overrides: Record<string, Override> } {
  try {
    const v = JSON.parse(localStorage.getItem(SESSION_KEY) || "null");
    return { selected: Array.isArray(v?.selected) ? v.selected : [], overrides: v?.overrides ?? {} };
  } catch { return { selected: [], overrides: {} }; }
}

export default function JewelryReviewPage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const { platform, shop: activeShop } = useShopVertical();
  const types = useJewelryTypes();
  const typeLabel = (k: string | null) => (k ? types.find((t) => t.key === k)?.label ?? k : "Diğer");
  const { data: shotData, isLoading: productsLoading, refetch: refetchProducts } = useShotProducts();
  const products = shotData?.items ?? [];

  // ── Oturum: secim + degisiklikler (URL'den gelenler EKLENIR) ──
  const [selected, setSelected] = useState<string[]>(() => {
    const s = loadSession().selected;
    const fromUrl = (params.get("products") ?? "").split(",").map((x) => x.trim()).filter(Boolean);
    return [...new Set([...s, ...fromUrl])];
  });
  const [overrides, setOverrides] = useState<Record<string, Override>>(() => loadSession().overrides);
  useEffect(() => {
    try { localStorage.setItem(SESSION_KEY, JSON.stringify({ selected, overrides })); } catch { /* dolu */ }
  }, [selected, overrides]);
  // Taslagi olusturulmus urunun bu listede isi yok: onceden kalanlar acilista cikarilir
  useEffect(() => {
    const sent = Object.entries(overrides).filter(([, o]) => o.createdDraftId).map(([k]) => k);
    if (!sent.length) return;
    setSelected((s) => s.filter((k) => !sent.includes(k)));
    setOverrides((o) => { const n = { ...o }; for (const k of sent) delete n[k]; return n; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // URL'deki secim oturuma alindi; adres temizlenir (yenilemede tekrar eklenmesin)
  useEffect(() => {
    if (params.get("products")) { params.delete("products"); setParams(params, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Baska sekmede (Duzenle) taslak acildiysa donunce tazele
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible") void refetchProducts(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [refetchProducts]);
  const patch = (key: string, p: Partial<Override>) => setOverrides((o) => ({ ...o, [key]: { ...o[key], ...p } }));

  // ── Ortak veriler: sablonlar + secenek setleri ──
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [optionSets, setOptionSets] = useState<OptionSet[]>([]);
  useEffect(() => {
    void Promise.all([
      fetch(`${getBackendUrl()}/api/listing-templates`, { headers: authHeaders() }).then((r) => (r.ok ? r.json() : { data: [] })),
      fetch(`${getBackendUrl()}/api/option-sets`, { headers: authHeaders() }).then((r) => (r.ok ? r.json() : { data: [] })),
    ]).then(([t, o]) => {
      setTemplates((t?.data ?? []) as TemplateRow[]);
      setOptionSets((o?.data ?? []) as OptionSet[]);
    }).catch(() => {});
  }, []);
  const [lastTpl, setLastTpl] = useState<string>(() => { try { return localStorage.getItem(LAST_TPL_KEY) ?? ""; } catch { return ""; } });
  useEffect(() => { tplListeners.add(setLastTpl); return () => { tplListeners.delete(setLastTpl); }; }, []);
  // Sayfa acilirken okunur: yazarken diger kartlar kaymasin, sonraki acilista gelir
  const [lastMargin] = useState<number | null>(readLastMargin);

  // ── Coklu kargo profili: tum Etsy magazalarinin profilleri ──
  // Secim yoksa eski davranis: sablonun kargo profiliyle aktif magazada TEK liste.
  const shops = useShopStore((st) => st.shops);
  // Yalnizca SECILI magazanin profilleri: kopyalar bu magazada olusur
  const etsyShops = shops.filter((x) => x.id === activeShop?.id && x.platform === "etsy");
  const profilesByShop = useShippingProfilesByShop(etsyShops.map((x) => x.id));
  const [shipTargets, setShipTargets] = useState<ShipTarget[]>(() => {
    try { const v = JSON.parse(localStorage.getItem(SHIP_TARGETS_KEY) || "[]"); return Array.isArray(v) ? v : []; } catch { return []; }
  });
  useEffect(() => { try { localStorage.setItem(SHIP_TARGETS_KEY, JSON.stringify(shipTargets)); } catch { /* */ } }, [shipTargets]);
  // Artik olmayan magazalarin secimleri sayilmaz
  const activeTargets = shipTargets.filter((t) => etsyShops.some((x) => x.id === t.shopId));
  const toggleTarget = (t: ShipTarget) => setShipTargets((cur) => (cur.some((x) => targetKey(x) === targetKey(t))
    ? cur.filter((x) => targetKey(x) !== targetKey(t)) : [...cur, t]));
  const shopLabel = (id: string) => { const x = shops.find((y) => y.id === id); return x?.label || x?.shopName || "Mağaza"; };
  const profileTitle = (t: ShipTarget) => profilesByShop[t.shopId]?.profiles.find((x) => x.id === t.profileId)?.title ?? `#${t.profileId}`;

  // ── Urun gorselleri (uretilen konsept fotograflari) ──
  const shotQueries = useQueries({
    queries: selected.map((key) => {
      const [catalogId, categoryId] = key.split(":");
      return {
        queryKey: ["product-shots", catalogId, categoryId],
        staleTime: 60_000,
        queryFn: async (): Promise<Shot[]> => {
          const r = await fetch(`${getBackendUrl()}/api/product-shots?catalogId=${catalogId}&categoryId=${categoryId}`, { headers: authHeaders() });
          return r.ok ? ((await r.json()).data ?? []) as Shot[] : [];
        },
      };
    }),
  });
  const shotsOf = (key: string) => shotQueries[selected.indexOf(key)]?.data ?? [];

  /** Kartin tam durumu: varsayilanlar + kullanici degisiklikleri */
  function derive(item: ShotProduct) {
    const ov = overrides[item.key] ?? {};
    const product = item.product;
    const src = product?.source;
    // Sablon (kargo profili sablondan): secili > son secim (urun turune uyuyorsa) >
    // turune uyan "USA Cargo" > turune uyan herhangi > son secim
    const fitsType = (t: TemplateRow) => !t.productTypeKey || !item.typeKey || t.productTypeKey === item.typeKey;
    const last = templates.find((t) => t.id === lastTpl);
    const autoTpl = (last && fitsType(last) ? last : undefined)
      ?? templates.find((t) => t.productTypeKey === item.typeKey && /usa\s*cargo/i.test(t.name))
      ?? templates.find((t) => t.productTypeKey && t.productTypeKey === item.typeKey)
      ?? last ?? null;
    const template = ov.templateId === null ? null
      : (ov.templateId ? templates.find((t) => t.id === ov.templateId) ?? null : autoTpl);
    const rules = template?.attributes ?? {};
    const colors = product?.colorAvailability?.map((c) => c.color) ?? [];
    const vars: Record<string, string> = {
      urun: product?.name ?? "", marka: product?.brand ?? "", sku: src?.sku ?? "",
      materyal: src?.attributes?.["Materyal"] ?? src?.attributes?.["Material"] ?? "", renk: colors.join(", "),
      ozellikler: attributeLines(product),
    };
    const title = (ov.title ?? (rules.titlePattern ? applyPattern(rules.titlePattern, vars) : product?.name ?? "")).slice(0, TITLE_MAX);
    // Tek degerli eksenler (Materyal, Zincir Tipi…) urun OZELLIGIDIR: kalipta
    // {ozellikler} yoksa aciklamanin sonuna eklenir
    const withAttrs = (text: string) => {
      const lines = attributeLines(product);
      return lines && !text.includes(lines.split("\n")[0]) ? `${text.trim()}\n\n${lines}`.trim() : text;
    };
    const intro = ov.description
      ?? (rules.descriptionPattern
        ? (rules.descriptionPattern.includes("{ozellikler}")
          ? applyPattern(rules.descriptionPattern, { ...vars, aciklama: src?.descriptionText ?? "" })
          : withAttrs(applyPattern(rules.descriptionPattern, { ...vars, aciklama: src?.descriptionText ?? "" })))
        : withAttrs(src?.descriptionText || template?.description || ""));
    const tags = ov.tags ?? (template?.tags ?? []).slice(0, 13);
    const materials = (ov.materials ?? (template?.materials?.length ? template.materials : productMaterials(product))).slice(0, 13);
    // Fiyat hedef kardan: tum Etsy ucretleri dusuldukten sonra satisin marginPct'i kalir
    // Oncelik: urune ozel > en son girilen > sablon > varsayilan
    const marginPct = ov.marginPct ?? lastMargin ?? rules.targetMarginPct ?? DEFAULT_TARGET_MARGIN;
    const rule = marginRule(marginPct);
    const cost = productCost(product, true);
    const shipping = productShipping(product, true);
    const salePrice = rule.price(cost + shipping);
    const analysis = etsyProfit(salePrice, cost, shipping);
    const profit = analysis.net;
    const { variationSets, personalizationSets, chartImages } = resolveOptionSets(effectiveOptionRefs(product, template), optionSets, product);
    const allVariations = buildVariations({ product, cost, shipping, rule, variationSets, colors });
    // Kartta cikarilan degerler listeye girmez (tum degerler kartta gorunmeye devam eder)
    const variations = allVariations
      .map((v) => ({ ...v, options: v.options.filter((o) => !(ov.excludedOptions?.[v.name] ?? []).includes(o.label)) }))
      .filter((v) => v.options.length > 0);
    const personalization = ov.personalization !== undefined
      ? (ov.personalization.length ? { enabled: true as const, questions: ov.personalization as unknown as Array<Record<string, unknown>> } : null)
      : buildPersonalization(product, personalizationSets,
        // Liste fiyati gibi x2: %50 kampanya ek ucrete de uygulanirsa satis farki kalir
        (extra) => Math.max(0.2, Math.round((rule.price(cost + shipping + extra) - salePrice) * LIST_PRICE_FACTOR * 100) / 100));
    // Aciklama: giris (AI/elle) + sablonun alt metni (urunden doldurulur)
    const footer = rules.descriptionFooter?.trim()
      ? renderDescriptionFooter(rules.descriptionFooter, product, variations as never, personalization as never)
      : "";
    const description = footer ? `${intro.trim()}\n\n${footer}` : intro;
    const combos = variations.reduce((n, v) => n * v.options.length, 1);
    // En ucuz varyasyon: urun ayari > sablonun (kategorinin) varsayilani
    const entry: EntryVariant | null = ov.entryVariant !== undefined ? ov.entryVariant : (rules.entryVariant ?? null);
    const entryCombos = entryComboPrices(variations, salePrice, rule, entry);
    const cheapest = entryCombos.reduce<(typeof entryCombos)[number] | null>((m, c) => (!m || c.price < m.price ? c : m), null);
    const entryPrice = cheapest?.price ?? null;
    const entryAnalysis = cheapest ? etsyProfit(cheapest.price, cheapest.base - shipping, shipping) : null;
    const entryProfit = entryAnalysis?.net ?? null;
    // Etsy'de %50 indirim yapilacak: gonderilen liste fiyati x2
    const listPrice = Math.round(salePrice * LIST_PRICE_FACTOR * 100) / 100;
    // Gorseller: uretilen fotograflar (ana once) + sablonun chart'lari (+ istenirse tedarikci)
    const shots = shotsOf(item.key).map((s) => s.imageUrl);
    // Eklentide duzenlenen Etsy fotograf plani varsa: uretilenlerden sonra onun sirasi
    // (chart + kutuphaneden eklenenler; tedarikci fotografi yalniz istenirse)
    const plan = product?.listingAnalysis?.photoPlan;
    const planTail = plan?.length
      ? plan.filter((x) => x.source !== "supplier" || ov.includeSupplier).map((x) => x.url)
      : null;
    const defaults = planTail
      ? [...shots, ...planTail]
      : [...shots, ...(ov.includeSupplier ? src?.images ?? [] : []), ...trailingImages(template, chartImages)];
    const images = (ov.images ?? [...new Set(defaults)]).slice(0, 20);

    // Etsy kategorisi: secilen; secilmediyse ilhamlarda TEK BASINA en sik gecen. Esitlikte ya da
    // ilham yoksa otomatik secilmez: takı sablonlarinin kategorisi Charm Necklaces, bileklik oraya
    // dusuyordu (REX-127). Ilhamsiz urunde secenekler ayni turdeki urunlerin kategorileri.
    const ownChoices = (product?.inspirations ?? []).filter((x) => x.taxonomyId);
    const taxonomyChoices = ownChoices.length ? ownChoices : sameTypeTaxonomies(products.flatMap((p) => p.product?.inspirations ?? []), item.typeKey);
    const useTemplate = ov.taxonomyFrom === "__template";
    const picked = useTemplate ? null : taxonomyChoices.find((x) => x.listingId === ov.taxonomyFrom) ?? soleLeadingInspiration(ownChoices);
    const taxonomyId: number | null = picked?.taxonomyId ?? (useTemplate ? template?.taxonomyId ?? null : null);
    const taxonomySource: string = useTemplate ? "__template" : picked?.listingId ?? "";

    // Ortak "Etsy'ye hazir mi" kurallari (Listing Builder ve yayin ile ayni)
    const common = checkListingReadiness({
      title, description, price: salePrice, quantity: template?.quantity || 100, tags, imageUrls: images,
      taxonomyId, shippingProfileId: template?.shippingProfileId ?? null,
      variations, personalization,
    });
    const blockers: string[] = common.blockers
      .filter((b) => b.code !== "no_taxonomy" && b.code !== "no_shipping") // sablon satirinda ayrica soylenir
      .map((b) => b.message);
    const warnings: string[] = common.warnings.filter((w) => w.code !== "few_tags" && w.code !== "few_images" && w.code !== "no_tags").map((w) => w.message);
    // Takida etiket SART: AI icerik ya da elle
    if (tags.length === 0) blockers.push("Etiket yok — AI içerik üretin ya da ekleyin");
    void etsyLimitError;
    // Etsy'ye yuklemeye hazir: kargo profili sablondan, kategori ilham listesinden ya da elle secimden
    if (!template) blockers.push("Şablon yok — kargo profili için şablon seçin ya da oluşturun");
    // Coklu kargo secildiyse profil secimden gelir; sablondaki gerekmez
    // Kargo profili yoksa yayinda magazanin "USA cargo" profili kullanilir
    else if (!template.shippingProfileId && activeTargets.length === 0) warnings.push(`"${template.name}" şablonunda kargo profili yok — USA cargo kullanılacak`);
    if (!taxonomyId) blockers.push(ownChoices.length ? "Etsy kategorisi seçilmedi: ilham listeleri farklı kategorilerde, birini seçin" : "Etsy kategorisi seçilmedi: bu ürünün ilham listesi yok, kategoriyi seçin");
    if (tags.length > 0 && tags.length < 13) warnings.push(`${tags.length}/13 etiket`);
    // AI baslik/aciklama/etiket ilham listelerinden beslenir; yoksa uretim kapali
    if (!(product?.inspirations?.length || product?.keywordPool?.items.length)) warnings.push("İlham listesi ve anahtar kelime havuzu yok — AI başlık/açıklama/etiket üretilemez; katalogda ürüne ilham listesi ekleyin ya da eklentide havuz oluşturun");
    if (cost <= 0) warnings.push("Maliyet bilinmiyor — fiyat 0");
    if (images.length < 5) warnings.push(`${images.length} görsel (en az 5 önerilir)`);
    if (images.length > 20) warnings.push("Etsy en fazla 20 görsel alır");
    const draft = ov.createdDraftId ? { id: ov.createdDraftId } : item.drafts[0] ?? null;
    return {
      taxonomyId, taxonomyChoices, taxonomySource, materials, intro, footer,
      entry, entryCombos, entryPrice, entryProfit, entryMarginPct: entryAnalysis?.marginPct ?? null, listPrice,
      item, ov, product, template, title, description, tags, marginPct, analysis, cost, shipping, salePrice, profit,
      variations, allVariations, personalization, combos, images, blockers, warnings, draft, colors, chartImages,
    };
  }

  const baseRows = useMemo(
    () => selected.map((k) => products.find((p) => p.key === k)).filter((x): x is ShotProduct => !!x).map(derive),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected, products, overrides, templates, optionSets, lastTpl, activeTargets.length, shotQueries.map((q) => q.dataUpdatedAt).join(",")],
  );
  // Etsy kategori ozellikleri: kartin kategorisinden; elle/AI yoksa kural + sablon
  const taxMap = useTaxonomyPropsMap(baseRows.map((r) => r.taxonomyId));
  const rows = baseRows.map((r) => {
    const taxProps: EtsyTaxProp[] | undefined = r.taxonomyId ? taxMap.get(r.taxonomyId) : undefined;
    const etsyProps: ListingProp[] = r.ov.etsyProps ?? (taxProps
      ? baseProps(taxProps, {
          product: r.product, materials: r.materials, hasPersonalization: !!r.personalization?.questions.length,
          variations: r.variations, colors: r.colors,
        }, effectiveTemplateDefaults(r.template?.attributes, true))
      : []);
    return { ...r, taxProps, etsyProps, styles: r.ov.styles ?? [] };
  });
  type Row = (typeof rows)[number];

  // ── Islemler ──
  const [busy, setBusy] = useState<Record<string, "ai" | "save" | "props">>({});
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const setRowBusy = (k: string, v: "ai" | "save" | "props" | null) => setBusy((b) => { const n = { ...b }; if (v) n[k] = v; else delete n[k]; return n; });

  async function generateContent(r: Row, forced?: Strategy) {
    const src = r.product?.source;
    const assigned = assignedStrategy(r.item.key);
    const strategy = forced ?? r.ov.seo?.strategy ?? assigned;
    setRowBusy(r.item.key, "ai");
    try {
      // Tedarikcinin Turkce fiyat ekseni ("Zincir Tipi + Doğum Taşı Sayısı") Etsy'ye gitmesin
      const ax = await fetch(`${getBackendUrl()}/api/supplier-catalogs/${r.item.catalogId}/categories/${r.item.categoryId}/english-axis`, { method: "POST", headers: authHeaders() });
      const axb = await ax.json().catch(() => ({}));
      if (!ax.ok) throw new Error(axb.error || "varyasyon adları çevrilemedi");
      if (axb.changed) void refetchProducts();
      // Havuzu olmayan urun: once Etsy API aramasi + havuz + (ilham bossa) en iyi 10 ilham
      let pool = r.product?.keywordPool;
      let insp = r.product?.inspirations;
      if (!pool?.items.length) {
        const pr = await fetch(`${getBackendUrl()}/api/supplier-catalogs/${r.item.catalogId}/categories/${r.item.categoryId}/prepare-seo`, { method: "POST", headers: authHeaders() });
        const pb = await pr.json().catch(() => ({}));
        if (!pr.ok) throw new Error(pb.error || "anahtar kelime havuzu kurulamadı");
        pool = pb.pool; insp = pb.inspirations;
        void refetchProducts();
      }
      // Sunucu yeniden baslarken (deploy) istek "Failed to fetch" ile duser: 20 sn sonra bir kez daha
      const send = () => fetch(`${getBackendUrl()}/api/listing-content/seo`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          marketplace: "etsy", productName: r.product?.name, brand: r.product?.brand, sku: src?.sku,
          categoryPath: r.template?.category ?? undefined, colors: r.colors,
          attributes: src?.attributes, supplierDescription: src?.descriptionText,
          inspirations: insp?.map((x) => ({ title: x.title, tags: x.tags })),
          keywordPool: pool?.items,
          variations: r.variations.map((v) => `${v.name}: ${v.options.map((o) => o.label).join(", ")}`),
          templateId: r.template?.id,
          strategy,
        }),
      });
      const wait = () => new Promise((r2) => setTimeout(r2, 20000));
      let res = await send().catch(async () => { await wait(); return send(); });
      if ([502, 503, 504].includes(res.status)) { await wait(); res = await send(); }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error([body.error, body.details].filter(Boolean).join(" — ") || "Üretilemedi");
      patch(r.item.key, {
        title: String(body.data?.title ?? r.title).slice(0, TITLE_MAX),
        description: String(body.data?.descriptionText ?? r.intro),
        ...(Array.isArray(body.data?.tags) ? { tags: (body.data.tags as string[]).slice(0, 13) } : {}),
        ...(Array.isArray(body.data?.materials) && body.data.materials.length ? { materials: (body.data.materials as string[]).slice(0, 13) } : {}),
        seo: {
          strategy, assigned,
          aiTitle: String(body.data?.title ?? ""), aiTags: Array.isArray(body.data?.tags) ? (body.data.tags as string[]).slice(0, 13) : [],
          poolKeywords: pool?.keywords ?? [], poolTop: (pool?.items ?? []).slice(0, 10).map((x) => x.phrase),
          inspirationIds: (insp ?? []).map((x) => x.listingId), generatedAt: new Date().toISOString(),
        },
      });
      // Ayni tiklamada kalan Etsy ozelliklerini de yeni baslik/etiketle doldur
      await fillProps(r, { title: body.data?.title, tags: body.data?.tags, description: body.data?.descriptionText });
    } catch (e) { setError(`${r.item.name}: ${e instanceof Error ? e.message : "içerik üretilemedi"}`); }
    finally { setRowBusy(r.item.key, null); }
  }

  /** Kural + sablonla dolmayan Etsy ozelliklerini AI ile doldurur (elle secilenler korunur) */
  async function fillProps(r: Row, fresh?: { title?: string; tags?: string[]; description?: string }) {
    if (!r.taxProps) return;
    const { props, styles } = await aiProps({
      taxProps: r.taxProps, filled: r.etsyProps,
      productName: r.product?.name, title: fresh?.title ?? r.title, tags: fresh?.tags ?? r.tags,
      description: fresh?.description ?? r.description,
      attributes: r.product?.source?.attributes,
      inspirations: r.product?.inspirations?.map((x) => ({ title: x.title, tags: x.tags })),
    });
    patch(r.item.key, { etsyProps: mergeProps(r.etsyProps, props), ...(r.styles.length ? {} : { styles }) });
  }

  async function autoFillProps(r: Row) {
    setRowBusy(r.item.key, "props");
    try { await fillProps(r); }
    catch (e) { setError(`${r.item.name}: ${e instanceof Error ? e.message : "özellikler üretilemedi"}`); }
    finally { setRowBusy(r.item.key, null); }
  }

  /** Kartin taslak icerigi: yeni taslak olustururken ve var olani guncellerken ayni */
  function draftBody(r: Row) {
    const src = r.product?.source;
    return {
        // Kelimelerin ilk harfi buyuk (eski AI basliklari da duzelir)
        title: etsyTitleCase(r.title.trim()).slice(0, TITLE_MAX),
        description: r.description.trim(),
        tags: r.tags,
        // Etsy'de %50 indirim yapilacak: liste fiyati x2 gonderilir
        price: r.listPrice > 0 ? r.listPrice : undefined,
        quantity: r.template?.quantity || 100,
        imageUrls: r.images,
        // Urun fotograflarindan video: taslak olusunca sunucuda arka planda
        video: DRAFT_VIDEO,
        materials: r.materials,
        sku: src?.sku ?? null,
        category: r.template?.category ?? null,
        templateId: r.template?.id,
        // Kategori ilham listesinden gelebilir: sablondakinin onune gecer
        taxonomyId: r.taxonomyId ?? undefined,
        // Engel yok: Listing Builder'da "Hazir" (Etsy'ye yuklemeye hazir) gorunur
        status: "ready",
        variations: scaleVariationPrices(r.variations, LIST_PRICE_FACTOR),
        personalization: r.personalization,
        attributes: {
          marketplace: "etsy",
          // Etsy kategori ozellikleri ve stiller: yayinda Etsy'ye yazilir
          etsyProperties: r.etsyProps,
          ...(r.styles.length ? { styles: r.styles } : {}),
          // En ucuz varyasyon: kombinasyon basina (liste) fiyat
          ...(r.entryCombos.length ? { comboPrices: r.entryCombos.map((c) => ({ match: c.match, price: Math.round(c.price * LIST_PRICE_FACTOR * 100) / 100 })) } : {}),
          // SEO deneyi: strateji + elle duzeltildi mi (satis karsilastirmasi icin)
          ...(r.ov.seo ? { seo: { ...r.ov.seo, edited: etsyTitleCase(r.title.trim()) !== etsyTitleCase(r.ov.seo.aiTitle.trim()) || r.tags.join("|") !== r.ov.seo.aiTags.join("|") } } : {}),
          supplier: {
            catalogId: r.item.catalogId, categoryId: r.item.categoryId, sku: src?.sku ?? null,
            cost: r.cost, shipping: r.shipping, currency: "USD", productUrl: src?.url ?? null,
          },
          pricing: {
            marginPct: r.marginPct, salePrice: r.salePrice, profit: Math.round(r.profit * 100) / 100,
            etsyFees: Math.round(r.analysis.fees.total * 100) / 100,
            listPriceFactor: LIST_PRICE_FACTOR, listPrice: r.listPrice,
            entryVariant: r.entry, entryPrice: r.entryPrice, entryProfit: r.entryProfit,
          },
        },
    };
  }

  /** Var olan taslagi karttakiyle esitler: yalnizca FARKLI alanlar yazilir.
   *  attributes birlestirilir (taslaga sonradan eklenen anahtarlar korunur). */
  async function updateDraft(r: Row) {
    if (!r.draft) return;
    setRowBusy(r.item.key, "save");
    try {
      const url = `${getBackendUrl()}/api/listing-drafts/${r.draft.id}`;
      const cur = await fetch(url, { headers: authHeaders() }).then((x) => (x.ok ? x.json() : Promise.reject(new Error(`taslak okunamadı (${x.status})`))));
      const current = (cur?.data ?? cur) as Record<string, unknown>;
      const next = draftBody(r) as Record<string, unknown>;
      delete next.status; // Hazir/Taslak durumu sunucuda kurallardan turer
      delete next.video; // video ayrica: taslakta yoksa ya da gorseller degistiyse
      const { comboPrices: _drop, ...curAttrs } = (current.attributes ?? {}) as Record<string, unknown>;
      next.attributes = { ...curAttrs, ...(next.attributes as Record<string, unknown>) };
      const LABELS: Record<string, string> = {
        title: "başlık", description: "açıklama", tags: "etiketler", materials: "malzemeler", price: "fiyat",
        variations: "varyasyonlar", personalization: "kişiselleştirme", imageUrls: "görseller", taxonomyId: "kategori",
        templateId: "şablon", category: "şablon kategorisi", quantity: "stok", sku: "SKU", attributes: "fiyat bilgisi",
        video: "video",
      };
      // Anahtar sirasindan bagimsiz karsilastirma (attributes sunucuda farkli sirada donebilir)
      const stable = (x: unknown): unknown =>
        Array.isArray(x) ? x.map(stable)
          : x && typeof x === "object" ? Object.fromEntries(Object.keys(x).sort().filter((k) => (x as Record<string, unknown>)[k] !== undefined).map((k) => [k, stable((x as Record<string, unknown>)[k])]))
          : x ?? null;
      const same = (x: unknown, y: unknown) => JSON.stringify(stable(x)) === JSON.stringify(stable(y));
      const diff: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(next)) if (v !== undefined && !same(v, current[k])) diff[k] = v;
      // Videosu olmayan (eski) taslakta ya da gorseller degistiyse video uretilir
      if (!current.videoUrl || "imageUrls" in diff) diff.video = DRAFT_VIDEO;
      const changed = Object.keys(diff);
      if (!changed.length) { setInfo(`${r.item.name}: taslak zaten güncel.`); return; }
      const res = await fetch(url, {
        method: "PATCH", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify(diff),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.message || b.error || `kaydedilemedi (${res.status})`);
      }
      const names = [...new Set(changed.map((k) => LABELS[k] ?? k))].join(", ");
      const onEtsy = !!current.etsyListingId;
      setInfo(`${r.item.name}: güncellendi — ${names}.${onEtsy ? " Etsy'deki ilana göndermek için Listing Builder'da taslağı açıp Etsy'yi güncelle." : ""}`);
    } catch (e) {
      setError(`${r.item.name}: ${e instanceof Error ? e.message : "taslak güncellenemedi"}`);
    } finally { setRowBusy(r.item.key, null); }
  }

  /** Hedef magazanin sablonlari (kopya o magazanin iade/isleme profillerini alir) */
  const shopTemplates = useRef<Record<string, TemplateRow[]>>({});
  async function templatesOf(shopId: string): Promise<TemplateRow[]> {
    if (shopTemplates.current[shopId]) return shopTemplates.current[shopId];
    const res = await fetch(`${getBackendUrl()}/api/listing-templates`, { headers: authHeaders({ "X-Shop-Id": shopId }) });
    const list = res.ok ? (((await res.json()).data ?? []) as TemplateRow[]) : [];
    shopTemplates.current[shopId] = list;
    return list;
  }

  /** Listenin bir kopyasini hedef magazada, secilen kargo profiliyle olustur.
   *  Baska magazada: ayni urun tipindeki (yoksa ayni adli) sablon eslenir —
   *  iade/isleme profili ve bolum kimlikleri magazaya ozeldir. Donen metin uyaridir. */
  async function createCopy(r: Row, t: ShipTarget): Promise<string | null> {
    const body = draftBody(r) as Record<string, unknown>;
    body.shippingProfileId = t.profileId;
    let note: string | null = null;
    if (t.shopId !== activeShop?.id) {
      const list = await templatesOf(t.shopId);
      const typeKey = r.template?.productTypeKey || r.item.typeKey;
      const match = (typeKey ? list.find((x) => x.productTypeKey === typeKey) : undefined)
        ?? (r.template ? list.find((x) => x.name === r.template!.name) : undefined) ?? null;
      body.templateId = match?.id;
      body.category = match?.category ?? body.category;
      if (!match) note = `${shopLabel(t.shopId)}: eşleşen şablon yok — iade/işleme profili eksik, taslağı o mağazada tamamla`;
    }
    const res = await fetch(`${getBackendUrl()}/api/listing-drafts`, {
      method: "POST", headers: authHeaders({ "X-Shop-Id": t.shopId }), body: JSON.stringify(body),
    });
    if (!res.ok) {
      const b = await res.json().catch(() => ({}));
      // Bu SKU bu profilde zaten listeli: kopya acilmaz, digerleri devam eder
      if (b.error === "SHIPPING_PROFILE_TAKEN") return `${profileTitle(t)} atlandı — ${b.message}`;
      throw new Error(`${shopLabel(t.shopId)} / ${profileTitle(t)}: ${b.message || b.error || `oluşturulamadı (${res.status})`}`);
    }
    return note;
  }

  async function createDraft(r: Row): Promise<boolean> {
    if (r.blockers.length) return false;
    setRowBusy(r.item.key, "save");
    try {
      if (activeTargets.length === 0) {
        await apiClient.createListingDraft(draftBody(r) as Parameters<typeof apiClient.createListingDraft>[0]);
      } else {
        // Secilen her kargo profili icin bir kopya; yarida kalirsa olusanlar atlanir
        const done = new Set(r.ov.copiedTargets ?? []);
        const notes: string[] = [];
        for (const t of activeTargets) {
          if (done.has(targetKey(t))) continue;
          const note = await createCopy(r, t);
          if (note) notes.push(note);
          done.add(targetKey(t));
          patch(r.item.key, { copiedTargets: [...done] });
        }
        if (notes.length) setError(`${r.item.name}: ${notes.join(" · ")}`);
      }
      // Liste hazir: urun denetim listesinden cikar (taslak Listing Builder'da)
      setSelected((s) => s.filter((k) => k !== r.item.key));
      setOverrides((o) => { const n = { ...o }; delete n[r.item.key]; return n; });
      if (r.template) { setLastTpl(r.template.id); try { localStorage.setItem(LAST_TPL_KEY, r.template.id); } catch { /* */ } }
      return true;
    } catch (e) {
      setError(`${r.item.name}: ${e instanceof Error ? e.message : "taslak oluşturulamadı"}`);
      return false;
    } finally { setRowBusy(r.item.key, null); }
  }

  // Kategori alani sonradan eklendi: eski ilham kayitlari (taxonomyId hic
  // yazilmamis) Etsy'den bir kez tamamlanir. Alinamazsa sunucu null yazar,
  // bir daha denenmez.
  const [refreshedKeys] = useState(() => new Set<string>());
  useEffect(() => {
    const stale = rows.filter((r) => !refreshedKeys.has(r.item.key)
      && (r.product?.inspirations ?? []).some((x) => x.taxonomyId === undefined || (x.taxonomyId && !x.taxonomyPath)));
    if (!stale.length) return;
    stale.forEach((r) => refreshedKeys.add(r.item.key));
    void Promise.all(stale.map((r) => fetch(
      `${getBackendUrl()}/api/supplier-catalogs/${r.item.catalogId}/categories/${r.item.categoryId}/inspirations/refresh`,
      { method: "POST", headers: authHeaders() },
    ).catch(() => null))).then(() => { void refetchProducts(); void qc.invalidateQueries({ queryKey: ["supplier-catalogs"] }); });
  }, [rows, refreshedKeys, refetchProducts, qc]);

  /** En ucuz varyasyonu sablonun (kategorinin) varsayilani yap */
  async function saveEntryDefault(t: TemplateRow | null, entry: EntryVariant) {
    if (!t) return;
    const attributes = { ...(t.attributes ?? {}), entryVariant: entry };
    const res = await fetch(`${getBackendUrl()}/api/listing-templates/${t.id}`, {
      method: "PATCH", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify({ attributes }),
    });
    if (!res.ok) { setError("Şablona kaydedilemedi"); return; }
    setTemplates((ts) => ts.map((x) => (x.id === t.id ? { ...x, attributes } : x)));
  }

  const readyRows = rows.filter((r) => r.blockers.length === 0 && !r.ov.createdDraftId);
  const needAi = rows.filter((r) => r.tags.length === 0 && !r.ov.createdDraftId);
  const [bulk, setBulk] = useState<null | "ai" | "save">(null);
  // Otomatik AI (29/10): anahtar acikken iceriksiz gelen urunler sirayla yazilir (Toplu Studio'dan gelenler dahil).
  // Hata veren urun bu oturumda bir daha denenmez (sonsuz tekrar ve maliyet olmasin).
  const [autoAi, setAutoAi] = useState(() => { try { return localStorage.getItem(AUTO_AI_KEY) === "1"; } catch { return false; } });
  useEffect(() => { try { localStorage.setItem(AUTO_AI_KEY, autoAi ? "1" : "0"); } catch { /* */ } }, [autoAi]);
  const autoTried = useRef(new Set<string>());
  useEffect(() => {
    if (!autoAi || bulk !== null || productsLoading) return;
    const todo = needAi.filter((r) => !autoTried.current.has(r.item.key) && !busy[r.item.key]);
    if (!todo.length) return;
    todo.forEach((r) => autoTried.current.add(r.item.key));
    setBulk("ai");
    void (async () => { for (const r of todo) await generateContent(r); setBulk(null); })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAi, bulk, productsLoading, needAi.map((r) => r.item.key).join(",")]);
  // Toplu Studio baska sekmede urun gonderince liste yenilenir
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === SESSION_KEY) setSelected((cur) => [...new Set([...cur, ...loadSession().selected])]);
      if (e.key === AUTO_AI_KEY) setAutoAi(e.newValue === "1"); // Toplu Studio'dan acildi/kapandi
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  async function bulkAi() {
    if (!confirm(`${needAi.length} ürün için AI ile başlık/açıklama/etiket üretilecek (her biri bir metin isteği). Devam edilsin mi?`)) return;
    setBulk("ai");
    for (const r of needAi) await generateContent(r);
    setBulk(null);
  }
  async function bulkCreate() {
    setBulk("save"); setError(null);
    let n = 0;
    for (const r of readyRows) if (await createDraft(r)) n++;
    setBulk(null);
    setInfo(`${n} liste Etsy'ye yüklemeye hazır — Listing Builder'da "Hazır" sekmesinde.`);
    void qc.invalidateQueries({ queryKey: ["shot-products"] });
  }

  const remove = (key: string) => {
    setSelected((s) => s.filter((k) => k !== key));
    setOverrides((o) => { const n = { ...o }; delete n[key]; return n; });
  };

  // ── Urun secici ──
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pq, setPq] = useState("");
  const [ptype, setPtype] = useState("");
  const [pOnlyNew, setPOnlyNew] = useState(true);
  const pickable = products.filter((p) => !selected.includes(p.key) && !p.product?.discontinued
    && (!ptype || (p.typeKey ?? "") === ptype)
    && (!pOnlyNew || p.drafts.length === 0)
    && (!pq.trim() || p.name.toLowerCase().includes(pq.trim().toLowerCase())
      || (p.product?.source?.sku ?? "").toLowerCase().includes(pq.trim().toLowerCase())));

  const [lightbox, setLightbox] = useState<{ urls: string[]; index: number } | null>(null);

  if (platform && platform !== "etsy") {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          Takı Liste Denetimi şimdilik Etsy mağazaları için. Trendyol'da kategori özellikleri ürün başına eşleştirildiği için Listing Builder'ı kullanın.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <ClipboardCheck className="h-5 w-5 text-fuchsia-500" /> Liste Denetimi — Takı
          </h1>
          <p className="mt-1 text-sm text-gray-500">Fotoğrafı üretilmiş ürünleri gözden geçir, eksikleri tamamla, taslağa çevir. Değişiklikler bu tarayıcıda saklanır.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <button onClick={() => setPickerOpen((v) => !v)} className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 font-medium text-gray-700 hover:bg-gray-50">
            <Plus className="h-4 w-4" /> Ürün ekle
          </button>
          <label className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 font-medium text-gray-700"
            title="Açıkken: içeriği olmayan ürünler (Toplu Studio'dan gelenler dahil) sırayla AI ile yazılır — havuzu yoksa önce Etsy'de aranır. Sayfa açıkken çalışır.">
            <input type="checkbox" checked={autoAi} onChange={(e) => setAutoAi(e.target.checked)} className="h-4 w-4 accent-fuchsia-600" />
            AI ile otomatik yaz
          </label>
          {needAi.length > 0 && (
            <button onClick={() => void bulkAi()} disabled={bulk !== null}
              className="flex items-center gap-1.5 rounded-lg border border-fuchsia-300 bg-white px-3 py-2 font-medium text-fuchsia-700 hover:bg-fuchsia-50 disabled:opacity-50">
              {bulk === "ai" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Eksik içerikleri AI ile üret ({needAi.length})
            </button>
          )}
          <button onClick={() => void bulkCreate()} disabled={bulk !== null || readyRows.length === 0}
            className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
            {bulk === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Hazır olanlardan taslak oluştur ({readyRows.length}{activeTargets.length > 1 ? ` × ${activeTargets.length} kargo` : ""})
          </button>
        </div>
      </div>

      {/* Coklu kargo profili: secilen her profil icin liste o profilin magazasinda cogaltilir */}
      {etsyShops.length > 0 && (
        <div className="mb-4 rounded-xl border border-gray-200 bg-white p-3 text-xs">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 font-semibold text-gray-800">
              <Truck className="h-4 w-4 text-sky-600" /> Kargo profilleri
              <span className="font-normal text-gray-500">
                {activeTargets.length === 0
                  ? "— seçim yok: şablondaki profille bu mağazada tek liste"
                  : `— her ürün ${activeTargets.length} liste olarak çoğaltılır`}
              </span>
            </p>
            {activeTargets.length > 0 && (
              <button onClick={() => setShipTargets([])} className="text-[11px] text-gray-500 hover:text-gray-700">Seçimi temizle</button>
            )}
          </div>
          <div className="space-y-1.5">
            {etsyShops.map((sh) => {
              const st = profilesByShop[sh.id];
              return (
                <div key={sh.id} className="flex flex-wrap items-center gap-1.5">
                  {st?.loading ? <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
                    : !st?.profiles.length ? <span className="text-gray-400">profil alınamadı</span>
                    : st.profiles.map((pf) => {
                      const t = { shopId: sh.id, profileId: pf.id };
                      const on = activeTargets.some((x) => targetKey(x) === targetKey(t));
                      return (
                        <button key={pf.id} onClick={() => toggleTarget(t)}
                          className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${on ? `${shippingProfileColor(pf.id)} font-semibold ring-1 ring-current` : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"}`}>
                          {on && <CheckCircle2 className="h-3 w-3" />}{pf.title}
                        </button>
                      );
                    })}
                </div>
              );
            })}
          </div>
          {activeTargets.some((t) => t.shopId !== activeShop?.id) && (
            <p className="mt-2 text-[10px] text-gray-400">
              Başka mağazadaki kopya o mağazanın aynı ürün tipindeki şablonunu (iade, işleme süresi, bölüm) kullanır; şablon yoksa taslak orada eksik kalır.
            </p>
          )}
        </div>
      )}

      {error && (
        <div className="mb-3 flex items-start justify-between rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <span>{error}</span><button onClick={() => setError(null)}><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
      {info && (
        <div className="mb-3 flex items-start justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
          <span>{info} <Link to="/listing-builder" className="underline">Listing Builder'a git</Link></span>
          <button onClick={() => setInfo(null)}><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {pickerOpen && (
        <div className="mb-5 rounded-xl border border-gray-200 bg-white p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
            <select value={ptype} onChange={(e) => setPtype(e.target.value)} className="rounded-lg border border-gray-200 px-2 py-1.5">
              <option value="">Tüm kategoriler</option>
              {[...new Set(products.map((p) => p.typeKey ?? ""))].map((k) => <option key={k || "none"} value={k}>{typeLabel(k || null)}</option>)}
            </select>
            <label className="flex items-center gap-1"><input type="checkbox" checked={pOnlyNew} onChange={(e) => setPOnlyNew(e.target.checked)} /> Yalnızca taslağı olmayanlar</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Ürün ara" className="w-48 rounded-lg border border-gray-200 py-1.5 pl-8 pr-2" />
            </div>
            <button onClick={() => setSelected((s) => [...s, ...pickable.map((p) => p.key)])} disabled={pickable.length === 0}
              className="ml-auto rounded-lg bg-fuchsia-600 px-3 py-1.5 font-medium text-white disabled:opacity-50">Hepsini ekle ({pickable.length})</button>
          </div>
          {productsLoading ? <Loader2 className="h-5 w-5 animate-spin text-gray-400" /> : pickable.length === 0 ? (
            <p className="text-xs text-gray-400">Eklenecek ürün yok. Ürün fotoğrafları Takı Studio'da üretilir.</p>
          ) : (
            <div className="grid max-h-80 grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-6 lg:grid-cols-8">
              {pickable.map((p) => (
                <button key={p.key} onClick={() => setSelected((s) => [...s, p.key])} title={p.name}
                  className="overflow-hidden rounded-lg border border-gray-200 text-left hover:border-fuchsia-400">
                  <img src={abs(p.coverUrl)} alt="" className="aspect-square w-full object-cover" loading="lazy" />
                  <p className="truncate px-1.5 py-1 text-[10px] text-gray-700">{p.name}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-12 text-center text-sm text-gray-500">
          Denetlenecek ürün yok. <button onClick={() => setPickerOpen(true)} className="text-fuchsia-600 underline">Ürün ekle</button> ya da Listing Builder'daki "Fotoğrafı üretilmiş ürünler"den gönder.
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((r) => (
            <ReviewCard key={r.item.key} r={r} templates={templates} busy={busy[r.item.key]} typeLabel={typeLabel(r.item.typeKey)}
              onPatch={(p) => patch(r.item.key, p)}
              onReset={() => setOverrides((o) => { const n = { ...o }; const keep = n[r.item.key]?.createdDraftId; delete n[r.item.key]; if (keep) n[r.item.key] = { createdDraftId: keep }; return n; })}
              onAi={(st) => void generateContent(r, st)}
              onAutoProps={() => void autoFillProps(r)}
              onCreate={() => void createDraft(r).then((ok) => {
                if (!ok) return;
                void qc.invalidateQueries({ queryKey: ["shot-products"] });
                setInfo(`${r.item.name}: taslak oluşturuldu — Listing Builder'da "Hazır" sekmesinde.`);
              })}
              onUpdateDraft={() => void updateDraft(r)}
              onRemove={() => remove(r.item.key)}
              onZoom={(i) => setLightbox({ urls: r.images.map(abs), index: i })}
              onSaveEntryDefault={(entry) => saveEntryDefault(r.template, entry)}
            />
          ))}
        </div>
      )}

      {lightbox && (
        <ImageLightbox urls={lightbox.urls} index={lightbox.index} onClose={() => setLightbox(null)}
          onIndex={(i) => setLightbox((l) => (l ? { ...l, index: i } : l))} />
      )}
    </div>
  );
}

/** Kartta kisisellestirme: ozet liste; "Düzenle" ile tam duzenleyici */
function PersonalizationBox({ questions, edited, onChange, onReset }: {
  questions: PersonalizationQuestion[];
  edited: boolean;
  onChange: (next: PersonalizationQuestion[]) => void;
  onReset: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="text-gray-600">
      <div className="flex items-center justify-between gap-2">
        <p>Kişiselleştirme: {questions.length ? `${questions.length} alan` : "yok"}{edited && <span className="ml-1 text-[10px] text-fuchsia-600">(elle düzenlendi)</span>}</p>
        <span className="flex gap-2 text-[11px]">
          {edited && <button type="button" onClick={onReset} className="text-gray-400 hover:text-gray-600" title="Şablon / ürün setlerinden yeniden oluştur">sıfırla</button>}
          <button type="button" onClick={() => setOpen((v) => !v)} className="text-indigo-600 hover:underline">{open ? "Kapat" : "Düzenle"}</button>
        </span>
      </div>
      {open ? (
        <div className="mt-1 rounded-lg border border-gray-200 p-2">
          <PersonalizationEditor questions={questions} onChange={onChange} accent="fuchsia" />
        </div>
      ) : questions.length > 0 && (
        <ul className="mt-1 space-y-1">
          {(questions as unknown as PersonalizationQuestionView[]).map((q, i) => (
            <li key={i} className="rounded border border-gray-100 bg-gray-50 px-2 py-1">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-gray-800">{q.question_text || "—"}</span>
                <span className="shrink-0 text-[10px] text-gray-500">
                  {QUESTION_TYPE_LABEL[q.question_type ?? ""] ?? q.question_type}
                  {q.required ? " · zorunlu" : " · isteğe bağlı"}
                  {q.max_allowed_characters ? ` · en fazla ${q.max_allowed_characters} karakter` : ""}
                </span>
              </div>
              {(q.options?.length ?? 0) > 0 && (
                <p className="mt-0.5 truncate text-[10px] text-gray-500" title={q.options!.map((o) => o.label).join(", ")}>
                  {q.options!.length} seçenek: {q.options!.slice(0, 8).map((o) => o.label).join(", ")}{q.options!.length > 8 ? "…" : ""}
                </p>
              )}
              {q.instructions && <p className="mt-0.5 text-[10px] text-gray-400">{q.instructions}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Etsy kisisellestirme sorusu (buildPersonalization ciktisi) — kartta gosterim icin */
interface PersonalizationQuestionView {
  question_type?: string;
  question_text?: string;
  required?: boolean;
  max_allowed_characters?: number;
  instructions?: string;
  options?: Array<{ label: string }>;
}
const QUESTION_TYPE_LABEL: Record<string, string> = {
  text_input: "metin", dropdown: "açılır liste", unlabeled_upload: "dosya yükleme",
};

type CardRow = {
  entry: EntryVariant | null; entryCombos: Array<{ match: Record<string, string>; price: number }>;
  entryPrice: number | null; entryProfit: number | null; entryMarginPct: number | null; listPrice: number;
  materials: string[]; intro: string; footer: string;
  taxonomyId: number | null; taxonomySource: string;
  taxonomyChoices: Array<{ listingId: string; title: string | null; taxonomyId?: number | null; taxonomyPath?: string | null }>;
  item: ShotProduct; ov: Override; template: TemplateRow | null; title: string; description: string; tags: string[];
  marginPct: number; analysis: ReturnType<typeof etsyProfit>; cost: number; shipping: number; salePrice: number; profit: number;
  variations: Array<{ name: string; options: Array<{ label: string }> }>; personalization: { questions: unknown[] } | null; combos: number;
  images: string[]; blockers: string[]; warnings: string[]; draft: { id: string; status?: string } | null;
  taxProps?: EtsyTaxProp[]; etsyProps: ListingProp[]; styles: string[];
  allVariations: Array<{ name: string; options: Array<{ label: string; sellingPrice?: number }> }>;
  chartImages: string[];
};

function ReviewCard({ r, templates, busy, typeLabel, onPatch, onReset, onAi, onAutoProps, onCreate, onUpdateDraft, onRemove, onZoom, onSaveEntryDefault }: {
  r: CardRow; templates: TemplateRow[]; busy?: "ai" | "save" | "props"; typeLabel: string;
  onPatch: (p: Partial<Override>) => void; onReset: () => void; onAi: (s?: Strategy) => void; onAutoProps: () => void; onCreate: () => void;
  onUpdateDraft: () => void;
  onRemove: () => void; onZoom: (i: number) => void;
  onSaveEntryDefault: (entry: EntryVariant) => Promise<void>;
}) {
  const [newTag, setNewTag] = useState("");
  const [newMat, setNewMat] = useState("");
  const [tplOpen, setTplOpen] = useState(false);
  const [descOpen, setDescOpen] = useState(false);
  const setImages = (next: string[]) => onPatch({ images: next });
  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= r.images.length) return;
    const n = [...r.images]; [n[i], n[j]] = [n[j], n[i]]; setImages(n);
  };
  const created = !!r.ov.createdDraftId;

  const noInspiration = !(r.item.product?.inspirations?.length || r.item.product?.keywordPool?.items.length);
  const pill2 = "flex items-center gap-1 rounded-full border-2 border-[#222] bg-white px-3 py-1 text-[13px] font-bold text-[#222] hover:bg-[#f6f6f5]";
  const inputCls = "w-full rounded-lg border border-[#c7c7c7] px-3 py-2 text-sm text-[#222] focus:border-[#222] focus:outline-none";
  const label = "text-sm font-semibold text-[#222]";

  return (
    <div className={`rounded-2xl border bg-white p-5 font-sans text-[#222] ${created ? "border-emerald-400" : r.blockers.length ? "border-red-300" : "border-[#e1e3df]"}`}>
      {/* Baslik satiri */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-lg font-bold text-[#222]" title={r.item.name}>{r.item.name}</p>
          <p className="text-[13px] text-[#595959]">
            {typeLabel} · {r.item.catalogName}{r.item.product?.source?.sku ? ` · ${r.item.product.source.sku}` : ""} · {r.item.shotCount} üretilmiş fotoğraf
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {r.draft && (
            <Link to={`/listing-builder?id=${r.draft.id}`} target="_blank"
              className="flex items-center gap-1 rounded-full bg-[#e6f4ea] px-3 py-1 text-[13px] font-semibold text-[#1e7d34] hover:underline">
              <CheckCircle2 className="h-3.5 w-3.5" /> {created ? "Taslak oluşturuldu" : "Önceden taslağı var"} <ExternalLink className="h-3 w-3" />
            </Link>
          )}
          <button onClick={() => window.open(`/listing-builder/jewelry?catalogId=${r.item.catalogId}&categoryId=${r.item.categoryId}`, "_blank")}
            title="Tek ürün Listing Builder'ında (yeni sekme) — varyasyonları tek tek düzenlemek için" className={pill2}>
            <Pencil className="h-3.5 w-3.5" /> Düzenle
          </button>
          <button onClick={onReset} title="Bu kartta yaptığın değişiklikleri geri al" className={pill2}>Sıfırla</button>
          <button onClick={onRemove} className={`${pill2} hover:text-red-600`}>Listeden çıkar</button>
        </div>
      </div>

      {/* Gorseller */}
      {/* Tum fotograflar gorunur: kaydirma yok, satir satir sarar */}
      <div className="mb-2 grid grid-cols-[repeat(auto-fill,minmax(104px,1fr))] gap-2">
        {r.images.map((u, i) => (
          <div key={u + i} className="group relative aspect-square overflow-hidden rounded-xl border border-[#e1e3df] bg-[#f6f6f5]">
            <button onClick={() => onZoom(i)} className="block h-full w-full"><img src={abs(u)} alt="" className="h-full w-full object-cover" loading="lazy" /></button>
            {i === 0 && <span className="absolute left-1 top-1 rounded-full bg-[#222] px-2 text-[10px] font-semibold text-white">Kapak</span>}
            <div className="absolute right-1 top-1 flex gap-0.5 opacity-0 transition group-hover:opacity-100">
              {i > 0 && (
                <button onClick={() => setImages([u, ...r.images.filter((_, j) => j !== i)])} title="Ana görsel yap" className="rounded bg-white/90 p-0.5 text-amber-500 shadow"><Star className="h-3 w-3" /></button>
              )}
              <button onClick={() => setImages(r.images.filter((_, j) => j !== i))} title="Listeden çıkar" className="rounded bg-white/90 p-0.5 text-red-500 shadow"><X className="h-3 w-3" /></button>
            </div>
            <div className="absolute bottom-1 left-1 right-1 flex justify-between opacity-0 transition group-hover:opacity-100">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="rounded bg-white/90 p-0.5 shadow disabled:opacity-30"><ChevronLeft className="h-3 w-3" /></button>
              <button onClick={() => move(i, 1)} disabled={i === r.images.length - 1} className="rounded bg-white/90 p-0.5 shadow disabled:opacity-30"><ChevronRight className="h-3 w-3" /></button>
            </div>
          </div>
        ))}
        {r.images.length === 0 && <p className="py-8 text-sm text-[#595959]">Görsel yok</p>}
      </div>
      <label className="mb-5 flex items-center gap-2 text-[13px] text-[#222]">
        <input type="checkbox" className="h-4 w-4 accent-[#222]" checked={!!r.ov.includeSupplier} onChange={(e) => onPatch({ includeSupplier: e.target.checked, images: undefined })} />
        Tedarikçi fotoğraflarını da ekle
      </label>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        {/* SOL: listeleme icerigi (Etsy duzenleyici bolumleri) */}
        <div className="min-w-0 space-y-4">
          <ReviewSection title="Başlık ve etiketler">
            <div className="mb-3">
              <div className="mb-1 flex items-baseline justify-between">
                <span className={label}>Başlık</span>
                <span className={`text-xs ${r.title.length > TITLE_MAX - 5 ? "text-amber-600" : "text-[#595959]"}`}>{r.title.length}/{TITLE_MAX}</span>
              </div>
              <input value={r.title} onChange={(e) => onPatch({ title: e.target.value.slice(0, TITLE_MAX) })} className={inputCls} />
              {titleWarnings(r.title).map((w) => (
                <p key={w} className="mt-1 text-xs text-amber-700">{w}</p>
              ))}
            </div>
            <div className="mb-3">
              <p className={`mb-1.5 ${label}`}>Etiketler <span className="font-normal text-[#595959]">({r.tags.length}/13)</span></p>
              <div className="flex flex-wrap gap-1.5">
                {r.tags.map((t) => (
                  <span key={t} className="flex items-center gap-1 rounded-full border border-[#c7c7c7] bg-white px-3 py-1 text-[13px] text-[#222]">
                    {t}<button onClick={() => onPatch({ tags: r.tags.filter((x) => x !== t) })}><X className="h-3 w-3" /></button>
                  </span>
                ))}
                {r.tags.length < 13 && (
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    const v = newTag.trim().toLowerCase().slice(0, 20);
                    if (v && !r.tags.includes(v)) onPatch({ tags: [...r.tags, v] });
                    setNewTag("");
                  }}>
                    <input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="+ etiket"
                      className="w-28 rounded-full border border-dashed border-[#c7c7c7] px-3 py-1 text-[13px]" />
                  </form>
                )}
              </div>
            </div>
            <div className="mb-3">
              <p className={`mb-1.5 ${label}`}>Malzemeler <span className="font-normal text-[#595959]">({r.materials.length}/13 — AI ürüne göre doldurur)</span></p>
              <div className="flex flex-wrap gap-1.5">
                {r.materials.map((m) => (
                  <span key={m} className="flex items-center gap-1 rounded-full border border-[#c7c7c7] bg-[#f6f6f5] px-3 py-1 text-[13px] text-[#222]">
                    {m}<button onClick={() => onPatch({ materials: r.materials.filter((x) => x !== m) })}><X className="h-3 w-3" /></button>
                  </span>
                ))}
                {r.materials.length < 13 && (
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    const v = newMat.trim().replace(/[^\p{L}\p{N} ]/gu, " ").replace(/\s+/g, " ").trim().slice(0, 45);
                    if (v && !r.materials.includes(v)) onPatch({ materials: [...r.materials, v] });
                    setNewMat("");
                  }}>
                    <input value={newMat} onChange={(e) => setNewMat(e.target.value)} placeholder="+ malzeme"
                      className="w-28 rounded-full border border-dashed border-[#c7c7c7] px-3 py-1 text-[13px]" />
                  </form>
                )}
              </div>
            </div>
            <div className="mb-3">
              <button onClick={() => setDescOpen((v) => !v)} className={`${label} hover:underline`}>
                Açıklama <span className="font-normal text-[#595959]">({r.description.length} karakter)</span> {descOpen ? "▲" : "▼"}
              </button>
              {descOpen && (
                <>
                  <textarea value={r.intro} onChange={(e) => onPatch({ description: e.target.value })} rows={8} className={`mt-1.5 ${inputCls}`} />
                  {/* Sablonun alt metni: girisin altina otomatik eklenir, burada yalniz gosterilir */}
                  {r.footer && (
                    <div className="mt-1.5 rounded-lg border border-dashed border-[#c7c7c7] bg-[#f6f6f5] p-3">
                      <p className="mb-1 text-xs font-semibold text-[#595959]">
                        + Alt metin ({r.template?.name} şablonundan, ürüne göre dolduruldu — otomatik eklenir)
                      </p>
                      <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap font-sans text-[13px] text-[#595959]">{r.footer}</pre>
                    </div>
                  )}
                </>
              )}
            </div>
            {/* AI icerik: havuz yoksa once Etsy API aramasiyla kurulur. Strateji sirayla atanir (SEO deneyi) */}
            {noInspiration && (
              <p className="mb-2 text-[12px] text-[#595959]">Anahtar kelime havuzu yok — AI ile yaz önce Etsy'de benzer listeleri arayıp havuz ve ilham listesini kurar (1-2 dk).</p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => onAi()} disabled={!!busy}
                title="Başlık, açıklama, etiket ve malzemeleri anahtar kelime havuzu ve ilham listelerine göre üretir"
                className="flex items-center gap-1.5 rounded-full border-2 border-[#222] bg-white px-4 py-1.5 text-sm font-bold text-[#222] hover:bg-[#f6f6f5] disabled:cursor-not-allowed disabled:opacity-40">
                {busy === "ai" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} AI ile başlık/açıklama/etiket
              </button>
              <span className="text-[12px] text-[#595959]">Strateji:</span>
              {STRATEGIES.map((st) => {
                const cur = (r.ov.seo?.strategy ?? assignedStrategy(r.item.key)) === st.key;
                const auto = assignedStrategy(r.item.key) === st.key;
                return (
                  <button key={st.key} type="button" disabled={!!busy} onClick={() => onAi(st.key)}
                    title={`${st.hint}${auto ? " — bu ürüne sırayla atanan" : " — tıkla: bu stratejiyle yeniden yaz"}`}
                    className={`rounded-full px-3 py-1 text-[12px] font-bold disabled:opacity-40 ${cur ? "bg-[#222] text-white" : "border border-[#d6d6d6] bg-white text-[#222] hover:bg-[#f6f6f5]"}`}>
                    {st.label}{auto ? " · atanan" : ""}
                  </button>
                );
              })}
            </div>
            {r.ov.seo && (
              <p className="mt-1.5 text-[11px] text-[#595959]">
                {r.ov.seo.strategy} stratejisiyle yazıldı{r.ov.seo.strategy !== r.ov.seo.assigned ? ` (atanan ${r.ov.seo.assigned}, elle değiştirildi)` : ""} · taslağa kaydedilir, satışlar stratejiye göre karşılaştırılır.
              </p>
            )}
          </ReviewSection>

          <ReviewSection title="Varyasyonlar"
            sub={r.variations.length ? `${r.variations.map((v) => `${v.name} (${v.options.length})`).join(" × ")} = ${r.combos} kombinasyon · tıkla: listeden çıkar / geri ekle` : "Varyasyon yok"}>
            {r.allVariations.map((v) => {
              const excluded = r.ov.excludedOptions?.[v.name] ?? [];
              const toggle = (lbl: string) => {
                const next = excluded.includes(lbl) ? excluded.filter((x) => x !== lbl) : [...excluded, lbl];
                // Eksenin tum degerleri cikarilamaz: en az biri kalir
                if (next.length >= v.options.length) return;
                onPatch({ excludedOptions: { ...(r.ov.excludedOptions ?? {}), [v.name]: next } });
              };
              return (
                <div key={v.name} className="mb-3">
                  <p className={`mb-1.5 ${label}`}>{v.name}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {v.options.map((o) => {
                      const off = excluded.includes(o.label);
                      return (
                        <button key={o.label} type="button" onClick={() => toggle(o.label)}
                          title={off ? "Listeye geri ekle" : "Listeden çıkar"}
                          className={`rounded-full border px-3 py-1 text-[13px] ${off ? "border-dashed border-[#c7c7c7] text-[#b5b5b5] line-through" : "border-[#222] bg-white text-[#222] hover:border-red-400"}`}>
                          {o.label}
                          {typeof o.sellingPrice === "number" && <span className={`ml-1 ${off ? "" : "text-[#595959]"}`}>${o.sellingPrice.toFixed(2)}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {/* En ucuz varyasyon: aramada gorunen "baslangic" fiyati */}
            {r.variations.length > 0 && (
              <div className="rounded-xl border border-[#f5d69a] bg-[#fdf0d5]/60 p-3 text-[13px]">
                <label className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-[#8a5a00]">En ucuz varyasyon (aramada görünen başlangıç fiyatı)</span>
                  <input type="checkbox" className="h-4 w-4 accent-[#222]" checked={!!r.entry}
                    onChange={(e) => onPatch({ entryVariant: e.target.checked ? { values: {}, marginPct: 5 } : null })} />
                </label>
                {r.entry && (
                  <div className="mt-2 space-y-1.5">
                    {r.variations.map((v) => (
                      <label key={v.name} className="flex items-center justify-between gap-2">
                        <span className="text-[#595959]">{v.name}</span>
                        <select value={r.entry!.values[v.name] ?? ""}
                          onChange={(e) => {
                            const values = { ...r.entry!.values };
                            if (e.target.value) values[v.name] = e.target.value; else delete values[v.name];
                            onPatch({ entryVariant: { ...r.entry!, values } });
                          }}
                          className="w-48 rounded-lg border border-[#c7c7c7] px-2 py-1">
                          <option value="">fark etmez</option>
                          {v.options.map((o) => <option key={o.label} value={o.label}>{o.label}</option>)}
                        </select>
                      </label>
                    ))}
                    <label className="flex items-center justify-between gap-2">
                      <span className="text-[#595959]" title="Bu kombinasyonlarda tüm Etsy ücretlerinden sonra kalacak net kâr">
                        Hedef kâr %{r.entry.marginPct == null && r.entry.multiplier ? ` (eski: ×${r.entry.multiplier})` : ""}
                      </span>
                      <input type="number" step="1" min="0" max="70" value={r.entry.marginPct ?? ""} placeholder="5"
                        onChange={(e) => onPatch({ entryVariant: { values: r.entry!.values, marginPct: Math.min(70, Math.max(0, Number(e.target.value) || 0)) } })}
                        className="w-20 rounded-lg border border-[#c7c7c7] px-2 py-1 text-right" />
                    </label>
                    {r.entryPrice != null ? (
                      <p className={r.entryProfit != null && r.entryProfit <= 0 ? "text-red-600" : "text-[#8a5a00]"}>
                        {r.entryCombos.length} kombinasyon · en ucuz ${r.entryPrice.toFixed(2)} (liste ${(r.entryPrice * LIST_PRICE_FACTOR).toFixed(2)})
                        {r.entryProfit != null && ` · net kâr $${r.entryProfit.toFixed(2)}${r.entryMarginPct != null ? ` (%${r.entryMarginPct.toFixed(1)})` : ""}`}
                      </p>
                    ) : (
                      <p className="text-[#595959]">Değer seçin — seçilen kombinasyonlar bu kâr hedefiyle fiyatlanır.</p>
                    )}
                    {r.template && Object.keys(r.entry.values).length > 0 && (
                      <button onClick={() => void onSaveEntryDefault(r.entry!)} className="font-semibold text-[#222] underline">
                        "{r.template.name}" şablonunun (kategori) varsayılanı yap
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </ReviewSection>

          <ReviewSection title="Kişiselleştirme">
            {/* Kisisellestirme: sablon/urun setlerinden; gerekirse duzenlenir */}
            <PersonalizationBox
              questions={(r.personalization?.questions ?? []) as unknown as PersonalizationQuestion[]}
              edited={r.ov.personalization !== undefined}
              onChange={(next) => onPatch({ personalization: next })}
              onReset={() => onPatch({ personalization: undefined })}
            />
          </ReviewSection>

          {/* Etsy onizlemesi: secili secenekler (cikarilan degerler haric), fiyatlarla */}
          <EtsyListingPreviewBox
            title={r.title} images={r.images} chartImages={r.chartImages} price={r.salePrice}
            variations={r.allVariations.map((v) => ({
              ...v,
              options: v.options.filter((o) => r.variations.find((x) => x.name === v.name)?.options.some((y) => y.label === o.label)),
            }))}
            personalization={r.personalization}
          />
        </div>

        {/* SAG: sablon, kategori, fiyat, ozellikler, durum */}
        <div className="min-w-0 space-y-4 text-[13px]">
          <ReviewSection title="Şablon ve kategori">
            <label className="mb-3 block">
              <span className={label}>Şablon</span>
              <select value={r.ov.templateId === null ? "__none" : r.template?.id ?? ""}
                onChange={(e) => {
                  // Sablon (kargo) degisince AI ile uretilen / elle yazilan baslik, aciklama
                  // ve etiketler KORUNUR; secim sonraki urunler icin hatirlanir
                  const v = e.target.value;
                  onPatch({ templateId: v === "__none" ? null : v || undefined });
                  if (v && v !== "__none") rememberTemplate(v);
                }}
                className={`mt-1 ${inputCls}`}>
                <option value="">Otomatik</option>
                <option value="__none">Şablonsuz</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.productTypeKey ? ` · ${t.productTypeKey}` : ""}</option>)}
              </select>
            </label>
            {r.template && (
              <button onClick={() => setTplOpen(true)} className="mb-3 text-[13px] font-semibold text-[#222] underline">
                Şablon ayrıntıları (tüm alanlar)
              </button>
            )}
            {tplOpen && r.template && <TemplateDetailsModal t={r.template} onClose={() => setTplOpen(false)} />}
            {/* Sablon yoksa ya da kargo profili eksikse: sablon olusturma sekmesine git, kaydedince buraya don */}
            {(!r.template || !r.template.shippingProfileId) && (
              <Link to={`/templates?new=1${r.item.typeKey ? `&productTypeKey=${encodeURIComponent(r.item.typeKey)}` : ""}${r.taxonomyId ? `&taxonomyId=${r.taxonomyId}` : ""}&returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                className="mb-3 flex items-center justify-center gap-1 rounded-full border-2 border-dashed border-[#222] px-3 py-1.5 text-[13px] font-semibold text-[#222] hover:bg-[#f6f6f5]">
                <Plus className="h-3.5 w-3.5" /> {r.template ? `"${r.template.name}" şablonunu düzenle / yeni şablon` : "Şablon oluştur (kargo profili ve diğer ayarlar)"}
              </Link>
            )}
            <label className="block">
              <span className={label}>Etsy kategorisi</span>
              {r.taxonomyChoices.length > 0 || r.template?.taxonomyId ? (
                <select value={r.taxonomySource} onChange={(e) => onPatch({ taxonomyFrom: e.target.value })} className={`mt-1 ${inputCls}`}>
                  {!r.taxonomySource && <option value="" disabled>Kategori seçin</option>}
                  {r.taxonomyChoices.map((x) => (
                    <option key={x.listingId} value={x.listingId}>
                      {(x.taxonomyPath ?? `#${x.taxonomyId}`)}{r.item.product?.inspirations?.some((i) => i.listingId === x.listingId) ? ` — ilham: ${(x.title ?? x.listingId).slice(0, 40)}` : ""}
                    </option>
                  ))}
                  {r.template?.taxonomyId && <option value="__template">Şablondaki kategori ({r.template.name})</option>}
                </select>
              ) : (
                <p className="mt-1 text-gray-600">Yok: kategorili ilham listesi ekleyince buradan seçilir</p>
              )}
            </label>
          </ReviewSection>

          <ReviewSection title="Fiyat">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[#595959]">Maliyet</span><span>${r.cost.toFixed(2)} + kargo ${r.shipping.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[#595959]" title="Tüm Etsy ücretleri düşüldükten sonra satış fiyatının bu yüzdesi net kâr kalır">Hedef kâr %</span>
                <input type="number" step="1" min="0" max="70" value={r.marginPct}
                  onChange={(e) => {
                    const v = Math.min(70, Math.max(0, Number(e.target.value) || 0));
                    onPatch({ marginPct: v });
                    if (v > 0) writeLastMargin(v);
                  }}
                  className="w-20 rounded-lg border border-[#c7c7c7] px-2 py-1 text-right" />
              </div>
              <div className="flex items-center justify-between text-[15px] font-bold">
                <span>Satış fiyatı</span><span>${r.salePrice.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between text-[#595959]"
                title={`İşlem $${r.analysis.fees.transaction.toFixed(2)} · Ödeme $${r.analysis.fees.payment.toFixed(2)} · Listeleme $${r.analysis.fees.listing.toFixed(2)} · Regulatory $${r.analysis.fees.regulatory.toFixed(2)}`}>
                <span>Etsy ücretleri</span><span>−${r.analysis.fees.total.toFixed(2)}</span>
              </div>
              <div className={`flex items-center justify-between font-semibold ${r.profit < 0 ? "text-red-600" : "text-[#1e7d34]"}`}>
                <span>Net kâr</span>
                <span>${r.profit.toFixed(2)} · marj %{r.analysis.marginPct.toFixed(1)} · maliyete %{r.analysis.markupPct.toFixed(0)}</span>
              </div>
              <div className="flex items-center justify-between text-[#595959]" title="Etsy'de %50 indirim yapılacağı için gönderilen fiyat">
                <span>Etsy liste fiyatı (×{LIST_PRICE_FACTOR})</span><span>${r.listPrice.toFixed(2)}</span>
              </div>
            </div>
          </ReviewSection>

          <div className="rounded-xl border border-[#e1e3df] p-3">
            <EtsyPropertiesEditor
              taxProps={r.taxProps} loading={!!r.taxonomyId && !r.taxProps}
              value={r.etsyProps} onChange={(next) => onPatch({ etsyProps: next })}
              variationNames={r.variations.map((v) => v.name)}
              styles={r.styles} onStylesChange={(next) => onPatch({ styles: next })}
              onAutoFill={onAutoProps} autoFilling={busy === "props" || busy === "ai"}
              collapsible defaultOpen={false}
            />
          </div>

          {(r.blockers.length > 0 || r.warnings.length > 0) && (
            <div className="space-y-1.5">
              {r.blockers.map((b) => <p key={b} className="flex items-start gap-1.5 rounded-lg bg-red-50 px-2.5 py-1.5 text-red-700"><XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{b}</p>)}
              {r.warnings.map((w) => <p key={w} className="flex items-start gap-1.5 rounded-lg bg-[#fdf0d5] px-2.5 py-1.5 text-[#8a5a00]"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{w}</p>)}
            </div>
          )}

          {r.draft && (
            <button onClick={onUpdateDraft} disabled={!!busy || !r.title.trim()}
              title="Karttaki içeriği mevcut taslağa yazar: başlık, açıklama, etiket, fiyat, varyasyon, görsel… — yalnızca değişen alanlar"
              className="flex w-full items-center justify-center gap-1.5 rounded-full bg-[#222] px-4 py-2.5 text-sm font-bold text-white hover:bg-black disabled:opacity-50">
              {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Taslağı güncelle (değişenleri yaz)
            </button>
          )}
          <button onClick={onCreate} disabled={!!busy || r.blockers.length > 0 || created}
            className={`flex w-full items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-bold disabled:opacity-50 ${r.draft && !created ? "border-2 border-[#222] bg-white text-[#222] hover:bg-[#f6f6f5]" : "bg-[#1e7d34] text-white hover:bg-[#186a2c]"}`}>
            {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            {created ? "Taslak oluşturuldu" : r.draft ? "Yine de yeni taslak oluştur" : "Taslak oluştur"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Kart icinde Etsy duzenleyici bolumu */
function ReviewSection({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-[#e1e3df] bg-white p-4">
      <h4 className="text-[16px] font-bold text-[#222]">{title}</h4>
      {sub ? <p className="mb-3 text-[13px] text-[#595959]">{sub}</p> : <div className="mb-3" />}
      {children}
    </section>
  );
}

/** Liste sablonunun TUM alanlari (salt okunur). Etsy kimlikleri magaza
 *  listelerinden okunur adlara cevrilir; duzenleme Sablonlar sayfasinda. */
function TemplateDetailsModal({ t, onClose }: { t: TemplateRow; onClose: () => void }) {
  const list = (path: string) => ({
    queryKey: ["etsy-list", path],
    queryFn: async () => {
      const res = await fetch(`${getBackendUrl()}/api/etsy/listings/${path}`, { headers: authHeaders() });
      if (!res.ok) return [] as Array<Record<string, unknown>>;
      const j = await res.json().catch(() => ({}));
      return (j.data?.results ?? j.results ?? []) as Array<Record<string, unknown>>;
    },
    staleTime: 5 * 60 * 1000,
  });
  const [ships, readiness, partners, policies, sections] = useQueries({
    queries: ["shipping-profiles/list", "readiness-states/list", "production-partners/list", "return-policies/list", "shop-sections/list"].map(list),
  }).map((q) => q.data ?? []);
  const nameOf = (rows: Array<Record<string, unknown>>, idKey: string, label: (x: Record<string, unknown>) => string, id?: string | null) => {
    if (!id) return "—";
    const row = rows.find((x) => String(x[idKey]) === String(id));
    return row ? label(row) : `#${id}`;
  };
  const rules = t.attributes ?? {};
  const fields: Array<[string, string]> = [
    ["Ad", t.name],
    ["Ürün tipi", t.productTypeKey || "—"],
    ["Etsy kategorisi (şablon)", t.taxonomyId ? `#${t.taxonomyId}` : "—"],
    ["Kargo profili", nameOf(ships, "shipping_profile_id", (x) => String(x.title ?? x.shipping_profile_id), t.shippingProfileId)],
    ["İşleme profili", nameOf(readiness, "readiness_state_id", (x) => {
      const d = x.min_processing_days != null && x.max_processing_days != null ? ` (${x.min_processing_days}-${x.max_processing_days} gün)` : "";
      return `${x.readiness_state_label ?? x.readiness_state}${d}`;
    }, t.processingProfileId)],
    ["Ürün ortağı", nameOf(partners, "production_partner_id", (x) => `${x.partner_name}${x.location ? ` - ${x.location}` : ""}`, t.productionPartnerId)],
    ["İade politikası", nameOf(policies, "return_policy_id", (x) => `${x.accepts_returns ? "İade" : "İade yok"} / ${x.accepts_exchanges ? "değişim" : "değişim yok"}`, t.returnPolicyId)],
    ["Mağaza bölümü", nameOf(sections, "shop_section_id", (x) => String(x.title ?? x.shop_section_id), t.sectionId)],
    ["Kim yaptı", t.whoMade || "—"],
    ["Ne zaman yapıldı", t.whenMade || "—"],
    ["Ne", t.whatIsIt || "—"],
    ["Tip", t.listingType || "—"],
    ["Malzeme (sarf) mi", t.isSupply ? "Evet" : "Hayır"],
    ["Otomatik yenileme", t.autoRenew === false ? "Manuel" : "Otomatik"],
    ["Öne çıkan", t.isFeatured ? "Evet" : "Hayır"],
    ["Stok", String(t.quantity ?? "—")],
    ["Hedef kâr (Etsy)", `%${rules.targetMarginPct ?? DEFAULT_TARGET_MARGIN}${rules.targetMarginPct == null ? " (varsayılan)" : ""}`],
    ["Başlık kalıbı", rules.titlePattern || "—"],
    ["Açıklama kalıbı", rules.descriptionPattern ? `${rules.descriptionPattern.slice(0, 160)}${rules.descriptionPattern.length > 160 ? "…" : ""}` : "—"],
    ["Etiketler", t.tags?.length ? t.tags.join(", ") : "—"],
    ["Malzemeler", t.materials?.length ? t.materials.join(", ") : "—"],
    ["Seçenek setleri", t.optionRefs?.length ? `${t.optionRefs.length} set` : "—"],
  ];
  const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-xl overflow-y-auto rounded-xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Şablon: {t.name}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="h-4 w-4" /></button>
        </div>
        <dl className="divide-y divide-gray-100 text-xs">
          {fields.map(([k, v]) => (
            <div key={k} className="grid grid-cols-3 gap-2 py-1.5">
              <dt className="text-gray-500">{k}</dt>
              <dd className="col-span-2 break-words text-gray-900">{v}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-4 flex justify-end">
          <Link to={`/templates?edit=${t.id}&returnTo=${returnTo}`}
            className="flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700">
            <Pencil className="h-3 w-3" /> Şablonu düzenle
          </Link>
        </div>
      </div>
    </div>
  );
}
